import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  useSensor,
  useSensors,
  useDroppable,
  PointerSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { Button } from '../../../components/ui';
import { cn } from '../../../lib/cn';

function DraggableTile({ id, lemma }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'px-4 py-2 rounded-lg border cursor-grab active:cursor-grabbing select-none',
        'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
        'text-gray-900 dark:text-white text-sm font-medium',
        'transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)]',
        isDragging && 'opacity-50'
      )}
    >
      {lemma}
    </div>
  );
}

// Registers an element as a @dnd-kit drop target so empty containers still
// accept drops. Highlights when a dragged item is hovering over it.
function DropZone({ id, className, hoverClassName, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn(className, isOver && (hoverClassName || 'ring-2 ring-blue-400/70'))}
    >
      {children}
    </div>
  );
}

export default function Stage3SynonymSort({
  columns,
  bank,
  onSubmit,
  // Controlled state for live-session bidir sync. When any of these is
  // undefined we fall back to component-local state, preserving the
  // uncontrolled component contract for future direct usage. The parent
  // (OnboardingActivity) provides all of them so it can broadcast each
  // drop and apply remote drops from a peer.
  assignments: ctrlAssignments,
  unplaced: ctrlUnplaced,
  columnItems: ctrlColumnItems,
  discarded: ctrlDiscarded,
  activeId: ctrlActiveId,
  feedback: ctrlFeedback,
  onAssignmentsChange,
  onUnplacedChange,
  onColumnItemsChange,
  onDiscardedChange,
  onActiveIdChange,
  onFeedbackChange,
  // Observer-side drag handler: when the teacher drags a tile in their
  // mirror view, this is called with the computed next-state object
  // {assignments, unplaced, columnItems, discarded}. The parent emits a
  // peer_intent carrying that shape; the student receives it, applies to
  // its own state, and rebroadcasts via the normal progress_state path
  // (which the teacher's mirror picks up). When unset, drops mutate
  // local state directly.
  onDragRequest,
}) {
  // assignments: { tileId: columnWordId | null }
  const [localAssignments, setLocalAssignments] = useState({});
  const [localUnplaced, setLocalUnplaced] = useState(() => bank.map((b) => b.id));
  const [localColumnItems, setLocalColumnItems] = useState(() =>
    Object.fromEntries(columns.map((c) => [c.word_id, []]))
  );
  const [localDiscarded, setLocalDiscarded] = useState([]);
  const [localActiveId, setLocalActiveId] = useState(null);
  const [localFeedback, setLocalFeedback] = useState(null);
  const [loading, setLoading] = useState(false);

  // Resolve controlled vs uncontrolled.
  const assignments = ctrlAssignments ?? localAssignments;
  const unplaced = ctrlUnplaced ?? localUnplaced;
  const columnItems = ctrlColumnItems ?? localColumnItems;
  const discarded = ctrlDiscarded ?? localDiscarded;
  const activeId = ctrlActiveId ?? localActiveId;
  const feedback = ctrlFeedback ?? localFeedback;

  const writeAssignments = (next) => {
    const value = typeof next === 'function' ? next(assignments) : next;
    if (ctrlAssignments === undefined) setLocalAssignments(value);
    onAssignmentsChange?.(value);
  };
  const writeUnplaced = (next) => {
    const value = typeof next === 'function' ? next(unplaced) : next;
    if (ctrlUnplaced === undefined) setLocalUnplaced(value);
    onUnplacedChange?.(value);
  };
  const writeColumnItems = (next) => {
    const value = typeof next === 'function' ? next(columnItems) : next;
    if (ctrlColumnItems === undefined) setLocalColumnItems(value);
    onColumnItemsChange?.(value);
  };
  const writeDiscarded = (next) => {
    const value = typeof next === 'function' ? next(discarded) : next;
    if (ctrlDiscarded === undefined) setLocalDiscarded(value);
    onDiscardedChange?.(value);
  };
  const writeActiveId = (next) => {
    const value = typeof next === 'function' ? next(activeId) : next;
    if (ctrlActiveId === undefined) setLocalActiveId(value);
    onActiveIdChange?.(value);
  };
  const writeFeedback = (next) => {
    const value = typeof next === 'function' ? next(feedback) : next;
    if (ctrlFeedback === undefined) setLocalFeedback(value);
    onFeedbackChange?.(value);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const bankById = Object.fromEntries(bank.map((b) => [b.id, b]));
  const allPlaced = unplaced.length === 0;
  const columnIdSet = new Set(columns.map((c) => String(c.word_id)));

  const findContainer = (id) => {
    if (unplaced.includes(id)) return 'bank';
    if (discarded.includes(id)) return 'discard';
    for (const [wordId, items] of Object.entries(columnItems)) {
      if (items.includes(id)) return wordId;
    }
    return null;
  };

  // Pure tile-move computation. Used by both the local drag handler and
  // the observer-side peer_intent path so the same logic governs both.
  const computeMove = (tileId, targetContainer) => {
    const activeContainer = findContainer(tileId);
    if (!targetContainer || String(activeContainer) === String(targetContainer)) {
      return null;
    }
    const nextUnplaced = unplaced.filter((x) => x !== tileId);
    const nextDiscarded = discarded.filter((x) => x !== tileId);
    const nextColumnItems = { ...columnItems };
    for (const key of Object.keys(nextColumnItems)) {
      nextColumnItems[key] = nextColumnItems[key].filter((x) => x !== tileId);
    }
    const nextAssignments = { ...assignments };

    if (targetContainer === 'bank') {
      delete nextAssignments[tileId];
      return {
        assignments: nextAssignments,
        unplaced: [...nextUnplaced, tileId],
        columnItems: nextColumnItems,
        discarded: nextDiscarded,
      };
    }
    if (targetContainer === 'discard') {
      nextAssignments[tileId] = null;
      return {
        assignments: nextAssignments,
        unplaced: nextUnplaced,
        columnItems: nextColumnItems,
        discarded: [...nextDiscarded, tileId],
      };
    }
    const wordId = parseInt(targetContainer, 10);
    nextAssignments[tileId] = wordId;
    nextColumnItems[wordId] = [...(nextColumnItems[wordId] || []), tileId];
    return {
      assignments: nextAssignments,
      unplaced: nextUnplaced,
      columnItems: nextColumnItems,
      discarded: nextDiscarded,
    };
  };

  const handleDragStart = (event) => writeActiveId(event.active.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    writeActiveId(null);
    if (!over) return;

    const overId = String(over.id);
    let targetContainer;
    if (overId === 'bank' || overId === 'discard' || columnIdSet.has(overId)) {
      targetContainer = overId;
    } else {
      targetContainer = findContainer(over.id);
    }
    if (!targetContainer) return;
    const move = computeMove(active.id, targetContainer);
    if (!move) return;

    // Observer mode: don't mutate local state — hand the computed move
    // up so the parent can route it through peer_intent. The student
    // applies it and rebroadcasts via progress_state, which is what the
    // observer mirror ultimately syncs to.
    if (onDragRequest) {
      onDragRequest(move);
      return;
    }
    writeAssignments(move.assignments);
    writeUnplaced(move.unplaced);
    writeColumnItems(move.columnItems);
    writeDiscarded(move.discarded);
  };

  const handleSubmit = async () => {
    if (!allPlaced || loading) return;
    setLoading(true);
    try {
      const result = await onSubmit(assignments);
      writeFeedback(result);
    } finally {
      setLoading(false);
    }
  };

  const activeTile = activeId ? bankById[activeId] : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6">
        {/* Instructions */}
        <p className="text-base text-gray-700 dark:text-gray-300 text-center">
          Drag each word from the bank into the box for the word it means.
          Put any words that don&rsquo;t belong into &ldquo;Doesn&rsquo;t belong.&rdquo;
        </p>

        {/* Column headers */}
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
        >
          {columns.map((col) => (
            <DropZone
              key={col.word_id}
              id={String(col.word_id)}
              className={cn(
                'min-h-[120px] rounded-lg border-2 border-dashed p-3 transition-colors',
                'border-gray-200 dark:border-gray-700',
                'bg-gray-50/50 dark:bg-gray-900/50'
              )}
              hoverClassName="border-blue-400 dark:border-blue-500 bg-blue-50/60 dark:bg-blue-900/20"
            >
              <h3 className="font-serif text-lg text-gray-900 dark:text-white text-center mb-3">
                {col.lemma}
              </h3>
              <SortableContext
                items={columnItems[col.word_id] || []}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {(columnItems[col.word_id] || []).map((tileId) => (
                    <DraggableTile
                      key={tileId}
                      id={tileId}
                      lemma={bankById[tileId]?.lemma}
                    />
                  ))}
                </div>
              </SortableContext>
            </DropZone>
          ))}
        </div>

        {/* Word bank */}
        <DropZone
          id="bank"
          className="rounded-lg p-2 transition-colors"
          hoverClassName="bg-blue-50/40 dark:bg-blue-900/10"
        >
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide font-medium">
            Word bank
          </p>
          <SortableContext items={unplaced} strategy={verticalListSortingStrategy}>
            <div className="flex flex-wrap gap-2 min-h-[36px]">
              {unplaced.length === 0 ? (
                <span className="text-xs text-gray-400 dark:text-gray-600 italic">
                  (empty)
                </span>
              ) : (
                unplaced.map((tileId) => (
                  <DraggableTile
                    key={tileId}
                    id={tileId}
                    lemma={bankById[tileId]?.lemma}
                  />
                ))
              )}
            </div>
          </SortableContext>
        </DropZone>

        {/* Discard zone */}
        <DropZone
          id="discard"
          className={cn(
            'min-h-[48px] rounded-lg border border-dashed p-3 text-center transition-colors',
            'border-gray-300 dark:border-gray-600',
            'text-xs text-gray-400 dark:text-gray-500',
            discarded.length > 0 && 'bg-gray-50 dark:bg-gray-900/50'
          )}
          hoverClassName="border-blue-400 dark:border-blue-500 bg-blue-50/60 dark:bg-blue-900/20"
        >
          {discarded.length === 0 ? (
            'Doesn’t belong'
          ) : (
            <SortableContext items={discarded} strategy={verticalListSortingStrategy}>
              <div className="flex flex-wrap gap-2 justify-center">
                {discarded.map((tileId) => (
                  <DraggableTile
                    key={tileId}
                    id={tileId}
                    lemma={bankById[tileId]?.lemma}
                  />
                ))}
              </div>
            </SortableContext>
          )}
        </DropZone>

        {/* Feedback */}
        {feedback && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-sm text-gray-600 dark:text-gray-400"
          >
            {feedback.result === 'yes' || feedback.pass
              ? 'Well done.'
              : `${feedback.correct || 0}/${feedback.total || 0} correct.`}
          </motion.div>
        )}

        {/* Submit */}
        <div className="flex justify-center">
          <Button onClick={handleSubmit} disabled={!allPlaced || loading}>
            {loading ? 'Checking...' : 'Submit'}
          </Button>
        </div>
      </div>

      <DragOverlay>
        {activeTile ? (
          <div className="px-4 py-2 rounded-lg border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm font-medium shadow-[0_4px_16px_rgba(0,0,0,0.12)]">
            {activeTile.lemma}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
